import {create} from "zustand";
import {ConfEnv} from "../config/config";
import privateRequest from "../Infra/http/private.request";
import publicRequest from "../Infra/http/public.request";
import {listMyFavoriteGamesService} from "../services/games/miniGames/favoriteGames.service";
import {isVisibleGame} from "../util/hiddenGames";
import {getQueryString} from "../util/query";
import {createOption} from "../util/text";
import {useAuthStore} from "./useAuth.store";
import useCurrencyStore from "./useCurrency.store";

//sort list by isFav true first
const sortGamesIsFav = list => {
  return list.sort((a, b) => {
    if (a.isFav && !b.isFav) {
      return -1;
    }
    if (!a.isFav && b.isFav) {
      return 1;
    }
    return 0;
  });
};

let latestFetchId = 0;

export const useMiniGames = create((set, get) => ({
  list: [],
  lastFetchKey: "",
  gameUrl: null,
  loading: false,
  error: null,
  providerDisplayNames: {},
  screenMode: window.innerWidth <= 768 ? "fullscreen" : "centered", // Mobile: fullscreen, Desktop: centered
  load: async () => {
    set({error: null});
  },
  unload: async () => {
    set({gameUrl: null});
  },
  setScreenMode: mode => {
    set({screenMode: mode});
  },
  resetScreenModeToDefault: () => {
    const defaultMode = window.innerWidth <= 768 ? "fullscreen" : "centered";
    set({screenMode: defaultMode});
  },
  search: "",
  filteredList: [],
  favoriteGames: [],
  favShow: false,
  provider: "",
  class: "",
  filtersVisible: false,
  providerOptions: [],
  classOptions: [],
  allProviderOptions: [],
  allClassOptions: [],
  gameCategories: [],
  setFiltersVisible: () => {
    const filtersVisible = !get().filtersVisible;
    set({filtersVisible});
  },
  resetFilters: () => {
    set({
      provider: "",
      class: "",
      favShow: false,
      search: "", // Reset search as well if needed
    });
  },
  setSearch: search => {
    set({search});

    // Filter the list only if there's a search term
    if (search) {
      const filteredList = get().list.filter(game => {
        return game.name.toLowerCase().includes(search.toLowerCase());
      });
      set({filteredList});
    } else {
      // If no search term, reset the filtered list to the original list
      set({filteredList: get().list});
    }
  },
  setFavoriteGames: (game, addFavoriteGame, deleteFavoriteGame) => {
    const {favoriteGames, list} = get();
    const provider = game.provider || game.game_provider;
    // Enrich from the games list (has same IDs, icons, names)
    const fullGame = list.find(g => g.id === game.id) || {};
    const existingGameIndex = favoriteGames.findIndex(
      item => item.game_id === game.id && item.game_provider === provider
    );

    if (existingGameIndex !== -1) {
      // Game already exists, remove it
      const existingFav = favoriteGames[existingGameIndex];
      const updatedFavoriteGames = [...favoriteGames];
      updatedFavoriteGames.splice(existingGameIndex, 1);
      set({favoriteGames: updatedFavoriteGames});

      // Update the game in the list to mark it as not favorite
      const updatedList = get().list.map(item => {
        if (item.id === game.id) {
          return {...item, isFav: false};
        }
        return item;
      });
      // Use the favorite record UUID (ID) for deletion, fall back to game.id
      deleteFavoriteGame(existingFav.ID || game.id);
      set({
        filteredList: sortGamesIsFav(updatedList),
        list: updatedList,
        favShow: false,
      });
    } else {
      // Game doesn't exist, add it
      set({
        favoriteGames: [
          ...favoriteGames,
          {
            game_id: game.id,
            game_provider: provider,
            icon: game.icon || fullGame.icon || "",
            name: game.name || fullGame.name || "",
          },
        ],
      });

      const updatedList = get().list.map(item => {
        if (item.id === game.id) {
          return {...item, isFav: true};
        }
        return item;
      });
      addFavoriteGame({game_id: game.id, game_provider: provider});
      set({filteredList: sortGamesIsFav(updatedList), list: updatedList});
    }
  },
  setFavShow: bool => {
    // Reset the provider and class to show all favs - user will be able to filter once in fav view
    set({provider: "", class: ""});
    if (bool) {
      // Filter the list to include only favorite games
      const filteredList = get().list.filter(game => game.isFav);
      set({filteredList});
    } else {
      // Apply provider and class filters when favShow is false
      const filteredList = get().list.filter(game => {
        const matchesProvider = !get().provider || game.provider === get().provider;
        const matchesClass = !get().class || game.class === get().class;
        return matchesProvider && matchesClass;
      });
      set({filteredList});
    }
    set({favShow: bool});
  },
  setProvider: option => {
    set({provider: option.value});
  },
  setClass: option => {
    set({class: option.value});
  },
  getConfig: async (provider, id) => {
    set({loading: true, error: false});

    try {
      // Get the selected currency from the currency store
      const selectedCurrency = useCurrencyStore.getState().selectedCurrency;
      const currencyParam = selectedCurrency ? selectedCurrency.id : "VES";
      const isMobile = window.innerWidth <= 768;

      const url = `${ConfEnv.API_URL.GAMES_INIT}/${provider}/${id}?currency=${currencyParam}&is_mobile=${isMobile}`;
      const response = await privateRequest.get(url);
      const {gameUrl = null} = response?.data?.data || "";
      set({gameUrl, loading: false});
    } catch (e) {
      set({loading: false, error: {message: e.message, code: e.status}});
    }
  },
  ensureFilterMetadata: async () => {
    if (get().allClassOptions.length > 0) return;

    const fetchId = ++latestFetchId;
    try {
      const response = await publicRequest.get(`${ConfEnv.API_URL.GAMES_LIST}${getQueryString({})}`);

      if (fetchId !== latestFetchId) return;

      const {game_providers: gameProviders = [], game_classes: gameClasses = [], game_categories: gameCategories = [], providers = []} =
        response.data.data;

      const providerOptions = gameProviders.map(item => createOption(item));
      const classOptions = gameClasses.map(item => createOption(item));

      const list = providers.reduce((acc, item) => {
        item.games.forEach(game => {
          if (isVisibleGame(game)) acc.push(game);
        });
        return acc;
      }, []);

      const displayNameFromGames = {};
      for (const game of list) {
        if (game.provider && game.provider_display_name) {
          displayNameFromGames[game.provider] = game.provider_display_name;
        }
      }

      const displayNameMap = providerOptions.reduce((acc, option) => {
        acc[option.value] = displayNameFromGames[option.value] ?? option.label;
        return acc;
      }, {});

      const providerOptionsWithDisplayNames = providerOptions.map(option => ({
        ...option,
        label: displayNameMap[option.value] ?? option.label,
      }));

      set({
        allProviderOptions: providerOptionsWithDisplayNames,
        allClassOptions: classOptions,
        gameCategories,
        providerOptions: providerOptionsWithDisplayNames,
        classOptions,
        providerDisplayNames: displayNameMap,
      });
    } catch (_e) {
      // Metadata fetch failed — dropdowns keep their last known values
    }
  },
  getMiniGames: async (provider, classFilter) => {
    const fetchId = ++latestFetchId;
    try {
      const authStatus = useAuthStore.getState().authStatus;
      let favGames = [];

      if (provider === "all") {
        provider = "";
      }

      const fetchKey = `${provider || ""}|${classFilter || ""}`;

      // Skip if the same data is already cached (not loading)
      if (fetchKey === get().lastFetchKey && get().list.length > 0 && !get().loading) {
        return;
      }

      // Skip if a fetch for the same key is already in flight
      // (prevents multiple components triggering parallel duplicate requests)
      if (fetchKey === get().lastFetchKey && get().loading) {
        return;
      }

      // Set loading to true before the API call
      set({loading: true});

      const queryString = getQueryString({
        provider: provider,
        class: classFilter,
      });

      const response = await publicRequest.get(`${ConfEnv.API_URL.GAMES_LIST}${queryString}`);

      // Abort if a newer fetch was started while this one was in-flight
      if (fetchId !== latestFetchId) {
        return;
      }

      const {game_providers: gameProviders = [], game_classes: gameClasses = [], game_categories: gameCategories = [], providers = []} =
        response.data.data;

      const providerOptions = gameProviders.map(item => createOption(item));
      const classOptions = gameClasses.map(item => createOption(item));

      // Construct the list from the API response
      const list = providers.reduce((acc, item) => {
        item.games.forEach(game => {
          if (isVisibleGame(game)) acc.push(game);
        });
        return acc;
      }, []);

      const displayNameFromGames = {};
      for (const game of list) {
        if (game.provider && game.provider_display_name) {
          displayNameFromGames[game.provider] = game.provider_display_name;
        }
      }

      const displayNameMap = providerOptions.reduce((acc, option) => {
        acc[option.value] = displayNameFromGames[option.value] ?? option.label;
        return acc;
      }, {});

      const providerOptionsWithDisplayNames = providerOptions.map(option => ({
        ...option,
        label: displayNameMap[option.value] ?? option.label,
      }));

      if (authStatus === "authenticated") {
        try {
          const favGamesResp = await listMyFavoriteGamesService();
          const rawFavs = favGamesResp?.data?.data || [];

          list.forEach(game => {
            const favGame = rawFavs.some(item => item.game_id === game.id && item.game_provider === game.provider);
            game.isFav = !!favGame;
          });

          favGames = rawFavs.map(fav => {
            const match = list.find(g => g.id === fav.game_id);
            return {
              ...fav,
              icon: match?.icon || fav.icon || "",
              name: match?.name || fav.game_name || "",
            };
          });
        } catch (_favError) {
          // Favorites failed — games still load without favorite markers
        }
      }

      const isFullCatalogFetch = !provider && !classFilter;
      const metadataUpdate = isFullCatalogFetch
        ? {
            providerOptions: providerOptionsWithDisplayNames,
            classOptions,
            allProviderOptions: providerOptionsWithDisplayNames,
            allClassOptions: classOptions,
            providerDisplayNames: displayNameMap,
          }
        : {};

      set({
        ...metadataUpdate,
        gameCategories,
        list,
        filteredList: sortGamesIsFav(list),
        favoriteGames: favGames,
        lastFetchKey: fetchKey,
        loading: false,
      });
    } catch (_e) {
      if (fetchId === latestFetchId) {
        set({loading: false});
      }
    }
  },
}));
